import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';

// ── Small, reusable mouse/touch UI primitives for the overlay scenes. ──
// Every panel scene (Settings, Map, Quests, Market, …) shares these so the panels + buttons look +
// feel the same. THEME: a cozy pixel-RPG wooden UI (solid warm panels, chunky 2-tone borders, cream
// text) to MATCH the pixel-art world — the old translucent emerald/gold "glass" read as generic AI
// UI bolted onto a pixel game. Pure Phaser game objects (no DOM) so they live in the overlay cleanly.
//
// The keys keep their legacy names (`emerald`, `gold`, `glass`) so the scenes need no edits — only the
// VALUES changed: `emerald` = the natural leaf-green primary, `gold` = warm amber, `glass` = the solid
// dark-wood panel fill, `glassStroke` = the inner wood bevel, `ink` = the chunky outer border.
export const UI = Object.freeze({
  emerald: 0x86c06a, // primary accent — natural leaf green (was neon 0x34d399)
  gold: 0xe0b15a, // secondary accent — warm amber
  glass: 0x2a2018, // panel fill — solid dark wood (drawn at high alpha → reads as a wooden box)
  glassStroke: 0x6b5436, // inner bevel — mid wood-brown
  ink: 0x140f0a, // chunky outer border — near-black wood
  textHex: '#f4ead2', // cream
  subHex: '#bda886', // muted tan
  goldHex: '#e0b15a',
  emeraldHex: '#86c06a',
});

/**
 * A premium rounded "glass" button with a label, hover/press feedback and a hand cursor.
 * Returns the container; call `.on('pointerup', …)` is wired internally via onClick.
 *
 * @param {Phaser.Scene} scene
 * @param {object} opts
 * @param {number} opts.x center x
 * @param {number} opts.y center y
 * @param {number} opts.width
 * @param {number} [opts.height=46]
 * @param {string} opts.label
 * @param {() => void} opts.onClick
 * @param {boolean} [opts.primary=false] emerald-filled primary style
 * @param {number} [opts.fontSize=20]
 * @returns {Phaser.GameObjects.Container}
 */
export function makeButton(scene, opts) {
  const { x, y, width, height = 46, label, onClick, primary = false, fontSize = 20 } = opts;
  const c = scene.add.container(x, y);
  const bg = scene.add.graphics();
  const radius = Math.min(6, height / 2); // chunky, near-square pixel-box corners

  const draw = (hover) => {
    bg.clear();
    const fill = primary ? UI.emerald : UI.glass;
    bg.fillStyle(fill, primary ? 1 : 0.97);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, radius);
    // chunky wooden bevel: a thick outer ink edge + a thin inner accent line (gold on hover)
    bg.lineStyle(3, UI.ink, 1);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, radius);
    bg.lineStyle(1.5, hover ? UI.gold : primary ? 0xbfe39c : UI.glassStroke, 1);
    bg.strokeRoundedRect(-width / 2 + 2.5, -height / 2 + 2.5, width - 5, height - 5, Math.max(1, radius - 2));
  };
  draw(false);

  const txt = scene.add
    .text(0, 0, label, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: `${fontSize}px`,
      color: primary ? '#03210f' : UI.textHex,
    })
    .setOrigin(0.5);

  c.add([bg, txt]);
  c.setSize(width, height);
  c.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
  c.on(Phaser.Input.Events.POINTER_OVER, () => {
    draw(true);
    scene.input.setDefaultCursor('pointer');
  });
  c.on(Phaser.Input.Events.POINTER_OUT, () => {
    draw(false);
    c.setScale(1);
    scene.input.setDefaultCursor('default');
  });
  // Fire on POINTER_DOWN (POINTER_UP is unreliable inside paused/overlay scenes — it was why
  // CLOSE / DONE / the volume steppers "didn't work"). Scale restores on UP/OUT.
  c.on(Phaser.Input.Events.POINTER_DOWN, () => {
    c.setScale(0.96);
    onClick?.();
  });
  c.on(Phaser.Input.Events.POINTER_UP, () => {
    c.setScale(1);
    scene.input.setDefaultCursor('default');
  });
  return c;
}

/**
 * A circular "✕" close button anchored at (x, y). Mouse-driven.
 * @param {Phaser.Scene} scene
 * @param {number} x
 * @param {number} y
 * @param {() => void} onClick
 * @returns {Phaser.GameObjects.Container}
 */
export function makeCloseButton(scene, x, y, onClick) {
  const c = scene.add.container(x, y);
  const r = 22;
  const bg = scene.add.graphics();
  const draw = (hover) => {
    bg.clear();
    bg.fillStyle(UI.glass, 0.97);
    bg.fillRoundedRect(-r, -r, r * 2, r * 2, 5);
    bg.lineStyle(3, UI.ink, 1);
    bg.strokeRoundedRect(-r, -r, r * 2, r * 2, 5);
    bg.lineStyle(1.5, hover ? UI.gold : UI.glassStroke, 1);
    bg.strokeRoundedRect(-r + 2.5, -r + 2.5, r * 2 - 5, r * 2 - 5, 3);
  };
  draw(false);
  const x2 = scene.add.text(0, 0, '✕', { fontFamily: 'system-ui, sans-serif', fontSize: '22px', color: UI.textHex }).setOrigin(0.5);
  c.add([bg, x2]);
  c.setSize(r * 2, r * 2);
  // Use a slightly larger square hit area than the visible circle so near-misses still register —
  // this is a small, important target and must be easy to hit on every click.
  c.setInteractive(new Phaser.Geom.Rectangle(-r - 4, -r - 4, (r + 4) * 2, (r + 4) * 2), Phaser.Geom.Rectangle.Contains);
  c.on(Phaser.Input.Events.POINTER_OVER, () => {
    draw(true);
    scene.input.setDefaultCursor('pointer');
  });
  c.on(Phaser.Input.Events.POINTER_OUT, () => {
    draw(false);
    scene.input.setDefaultCursor('default');
  });
  // Fire on POINTER_DOWN (reliable in paused/overlay scenes, unlike UP). Clear the cursor so it
  // doesn't get stuck as a hand after the overlay closes.
  c.on(Phaser.Input.Events.POINTER_DOWN, () => {
    scene.input.setDefaultCursor('default');
    onClick?.();
  });
  return c;
}

/**
 * A two-state pill toggle (ON / OFF). Calls onChange with the next boolean when clicked.
 * @param {Phaser.Scene} scene
 * @param {object} opts
 * @param {number} opts.x left x
 * @param {number} opts.y center y
 * @param {boolean} opts.value current state
 * @param {(next: boolean) => void} opts.onChange
 * @param {number} [opts.width=92]
 * @returns {{ container: Phaser.GameObjects.Container, set: (v: boolean) => void }}
 */
export function makeToggle(scene, opts) {
  const { x, y, onChange, width = 92 } = opts;
  let value = opts.value;
  const height = 36;
  const c = scene.add.container(x + width / 2, y);
  const bg = scene.add.graphics();
  const knob = scene.add.graphics();
  const label = scene.add
    .text(0, 0, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '15px' })
    .setOrigin(0.5);

  const render = () => {
    bg.clear();
    bg.fillStyle(value ? UI.emerald : 0x3a2e22, value ? 1 : 0.97);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, height / 2);
    bg.lineStyle(3, UI.ink, 1);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, height / 2);
    bg.lineStyle(1.5, value ? UI.gold : UI.glassStroke, 1);
    bg.strokeRoundedRect(-width / 2 + 2.5, -height / 2 + 2.5, width - 5, height - 5, (height - 5) / 2);
    knob.clear();
    knob.fillStyle(0xf4ead2, 1); // cream knob
    const kx = value ? width / 2 - height / 2 : -width / 2 + height / 2;
    knob.fillCircle(kx, 0, height / 2 - 5);
    label.setText(value ? 'ON' : 'OFF').setColor(value ? '#1a2410' : UI.subHex);
    label.setPosition(value ? -width / 2 + 18 : width / 2 - 18, 0);
  };
  render();

  c.add([bg, knob, label]);
  c.setSize(width, height);
  c.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
  c.on(Phaser.Input.Events.POINTER_OVER, () => scene.input.setDefaultCursor('pointer'));
  c.on(Phaser.Input.Events.POINTER_OUT, () => scene.input.setDefaultCursor('default'));
  // Fire on POINTER_DOWN (more reliable than POINTER_UP inside zoomed overlay scenes — the
  // switches "not working" was the up-event not landing on the toggle).
  c.on(Phaser.Input.Events.POINTER_DOWN, () => {
    value = !value;
    render();
    onChange?.(value);
  });

  return {
    container: c,
    set: (v) => {
      value = v;
      render();
    },
  };
}
